/**
 * Public marketing landing page at /ai-visibility/.
 *
 * If the visitor is already signed in we bounce them straight to their
 * dashboard.
 */

import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext.jsx";
import LanguageSwitcher from "../components/LanguageSwitcher.jsx";

export default function Landing() {
  const { isAuthenticated, bootstrapping } = useAuth();
  const { t } = useTranslation();

  if (bootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        {t("common.loading")}
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-brand-50 text-slate-900">
      <Header />
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Header() {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-10 bg-brand-50/90 backdrop-blur border-b border-brand-200/60">
      <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4">
        <Link to="/" className="flex items-center gap-2">
          <span className="mono text-xs tracking-widest text-brand-700">
            SEOGEEKS
          </span>
          <span className="font-semibold">AI Visibility</span>
        </Link>
        {/* The Sign-in link is hidden on the smallest screens to
            keep the header on one row — registration is the primary
            CTA anyway, and signed-up users have their own bookmarks. */}
        <nav className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher />
          <Link to="/login" className="btn-ghost hidden xs:inline-flex sm:inline-flex">
            {t("common.signIn")}
          </Link>
          <Link to="/register" className="btn-primary">
            {t("common.getStarted")}
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 lg:pt-20 pb-12 sm:pb-16 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
      <div>
        <span className="mono text-xs tracking-widest text-brand-700">
          {t("landing.tagline")}
        </span>
        <h1 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight">
          {t("landing.heroTitle")}{" "}
          <span className="text-brand-700">{t("landing.heroTitleAccent")}</span>.
        </h1>
        <p className="mt-5 text-lg text-slate-700 max-w-lg">
          {t("landing.heroSubtitle")}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/register" className="btn-primary">
            {t("landing.ctaPrimary")}
          </Link>
          <Link to="/login" className="btn-ghost">
            {t("landing.ctaSecondary")}
          </Link>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          {t("landing.ctaDisclaimer")}
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-slate-200 p-6">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          {t("landing.metricCoverage")}
        </div>
        <div className="mono text-5xl font-bold text-brand-700 mt-1">73%</div>
        <div className="mt-6 grid grid-cols-3 gap-4 text-center">
          {[
            { l: "OpenAI", v: "84%" },
            { l: "Anthropic", v: "61%" },
            { l: "Gemini", v: "73%" },
          ].map((m) => (
            <div key={m.l} className="rounded-lg bg-brand-50 py-3">
              <div className="text-xs text-slate-500">{m.l}</div>
              <div className="mono font-bold text-lg">{m.v}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 h-2 bg-brand-100 rounded-full overflow-hidden">
          <div className="h-full bg-brand-500" style={{ width: "73%" }} />
        </div>
        <div className="mt-2 flex justify-between text-xs text-slate-500">
          <span>{t("landing.metricShareOfVoice")}</span>
          <span>{t("landing.shareYouVsCompetitors")}</span>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const { t } = useTranslation();
  const items = [1, 2, 3, 4, 5, 6].map((n) => ({
    title: t(`landing.feature${n}Title`),
    body: t(`landing.feature${n}Body`),
  }));
  return (
    <section className="bg-white border-y border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
        <h2 className="text-2xl sm:text-3xl font-semibold">{t("landing.featuresTitle")}</h2>
        <p className="mt-2 text-slate-600 max-w-2xl">
          {t("landing.featuresSubtitle")}
        </p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((f) => (
            <div key={f.title} className="card">
              <h3 className="font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const steps = [1, 2, 3, 4].map((n) => ({
    n: String(n).padStart(2, "0"),
    title: t(`landing.how${n}Title`),
    body: t(`landing.how${n}Body`),
  }));
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
      <h2 className="text-2xl sm:text-3xl font-semibold">{t("landing.howTitle")}</h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((s) => (
          <div key={s.n}>
            <div className="mono text-brand-700 font-bold">{s.n}</div>
            <h3 className="mt-2 font-semibold text-slate-900">{s.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Pricing() {
  const { t } = useTranslation();
  return (
    <section className="bg-white border-y border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
        <h2 className="text-2xl sm:text-3xl font-semibold">{t("landing.pricingTitle")}</h2>
        <p className="mt-2 text-slate-600 max-w-2xl">
          {t("landing.pricingSubtitle")}
        </p>
        <div className="mt-10 grid sm:grid-cols-3 gap-6">
          {[
            { name: "Starter", tokens: 50, price: "199" },
            { name: "Growth", tokens: 200, price: "699", best: true },
            { name: "Pro", tokens: 600, price: "1799" },
          ].map((p) => (
            <div
              key={p.name}
              className={[
                "card flex flex-col gap-3",
                p.best ? "ring-2 ring-brand-500" : "",
              ].join(" ")}
            >
              <div className="flex justify-between items-start">
                <div className="text-sm text-slate-500">{p.name}</div>
                {p.best && (
                  <span className="text-xs bg-brand-100 text-brand-800 px-2 py-1 rounded-full">
                    {t("landing.pricingMostPopular")}
                  </span>
                )}
              </div>
              <div>
                <div className="mono text-3xl font-bold">{p.tokens}</div>
                <div className="text-xs text-slate-500">
                  {t("landing.pricingTokens")}
                </div>
              </div>
              <div className="mono text-xl">{p.price} UAH</div>
              <Link to="/register" className="btn-primary mt-auto">
                {t("common.getStarted")}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useTranslation();
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-24 text-center">
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold">
        {t("landing.finalCtaTitle")}
      </h2>
      <p className="mt-3 text-slate-600 max-w-xl mx-auto">
        {t("landing.finalCtaSubtitle")}
      </p>
      <Link to="/register" className="btn-primary mt-8 inline-block">
        {t("landing.finalCtaButton")}
      </Link>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 text-sm text-slate-500 flex flex-col sm:flex-row sm:flex-wrap sm:justify-between gap-3 sm:gap-4">
        <span>© {new Date().getFullYear()} SEOGeeks · AI Visibility</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/privacy" className="hover:text-slate-700">
            {t("legal.privacyShort")}
          </Link>
          <Link to="/terms" className="hover:text-slate-700">
            {t("legal.termsShort")}
          </Link>
          <Link to="/offer" className="hover:text-slate-700">
            {t("legal.offerShort")}
          </Link>
          <Link to="/login" className="hover:text-slate-700">
            {t("common.signIn")}
          </Link>
          <Link to="/register" className="hover:text-slate-700">
            {t("common.signUp")}
          </Link>
        </div>
      </div>
    </footer>
  );
}
